/**
 * BigList #8 (docs/audits/biglist/08-date-format.md): PB's `DisplayDateFormat` setting,
 * resolved once per SPA session and consumed by `pbDateTime.ts`'s `pbDate()`/`pbDateTime()`.
 *
 * ## Mechanism (see the audit doc for full PB source citations)
 *
 * PB reads `dbxx.rf_configuration` (section='General', parm='DisplayDateFormat') at login,
 * cascaded through a per-workstation override and the Windows locale before landing on this
 * DB row, with a hard `'mdy'` (`mm/dd/yyyy`) fallback if nothing resolves
 * (`uo_phfc_global_var_cntr.sru:272-312`). A web client has no per-workstation setting or OS
 * short-date format to read, so PH10 implements only the DB-config step (exposed on
 * `GET /api/config/features` as the raw `displayDateFormat` string) and the hard fallback.
 * At runtime, PB rewrites `editmask.mask` on every column whose mask is the literal token
 * `[date]` (`of_reset_formate`, `uo_phfc_crt_translate_util.sru:813-882`) — columns with a
 * literal mask (`mm/dd/yyyy`, `[shortdate]`, etc.) are untouched and must NOT be routed
 * through this setting.
 *
 * Live value confirmed on chris_900 / APEX_PROD900 (2026-09-04): `'MM/dd/yy'` — mixed case,
 * 2-digit year. The mapper below is therefore case-insensitive on the `mm`/`dd`/`yy`/`yyyy`
 * tokens and handles both 2- and 4-digit years generically, rather than hardcoding a fixed
 * lookup table of every case variant PB might send.
 */

/** PB's own hard fallback family — `xs_db_date_format[1] = 'mdy'` (uo_phfc_global_var_cntr.sru:311). */
export const FALLBACK_DISPLAY_DATE_FORMAT = 'MM/dd/yyyy';

const SEPARATOR = /[/\-.]/;

/**
 * Maps a raw PB edit-mask token (e.g. `mm/dd/yyyy`, `MM/dd/yy`, `dd/mm/yyyy`) to the token
 * vocabulary `pbDateTime.ts` renders with (`MM`/`dd`/`yyyy`/`yy`, date-fns-style). Accepts any
 * case on the `mm`/`dd`/`yyyy`/`yy` components and either a 2- or 4-digit year, since PB's own
 * validation (`f_phfc_check_display_date_format`) doesn't constrain the letter case and the
 * confirmed live value (`MM/dd/yy`) is itself mixed-case. Anything blank, malformed (wrong
 * segment count, an unrecognized token, missing a month or day component), or otherwise
 * unparseable falls back to PB's own `mdy` family rather than throwing or rendering garbage.
 */
export function mapPbDateToken(pbToken: string | null | undefined): string {
  const raw = (pbToken ?? '').trim();
  if (!raw) return FALLBACK_DISPLAY_DATE_FORMAT;

  const sepMatch = raw.match(SEPARATOR);
  const sep = sepMatch ? sepMatch[0] : '/';
  const parts = raw.split(SEPARATOR);
  if (parts.length !== 3) return FALLBACK_DISPLAY_DATE_FORMAT;

  const mapped = parts.map((part): string | null => {
    switch (part.toLowerCase()) {
      case 'mm':
        return 'MM';
      case 'dd':
        return 'dd';
      case 'yyyy':
        return 'yyyy';
      case 'yy':
        return 'yy';
      default:
        return null;
    }
  });

  if (mapped.some((m) => m === null)) return FALLBACK_DISPLAY_DATE_FORMAT;
  if (!mapped.includes('MM') || !mapped.includes('dd')) return FALLBACK_DISPLAY_DATE_FORMAT;

  return mapped.join(sep);
}

let resolvedFormat = FALLBACK_DISPLAY_DATE_FORMAT;

/**
 * Set once at auth-boot from the `GET /api/config/features` payload's raw `displayDateFormat`
 * (see `useSyncDisplayDateFormat` in `shared/config/useFeatures.ts`). Safe to call repeatedly —
 * React Query re-fires the sync effect on every features refetch, which is a no-op unless a
 * tenant's config value actually changed.
 */
export function setDisplayDateFormat(pbToken: string | null | undefined): void {
  resolvedFormat = mapPbDateToken(pbToken);
}

/** The resolved token every `[date]`-masked browse/detail column should render with. */
export function getDisplayDateFormat(): string {
  return resolvedFormat;
}

/** Test-only: restore the module-level default between specs (module state persists across tests otherwise). */
export function resetDisplayDateFormatForTests(): void {
  resolvedFormat = FALLBACK_DISPLAY_DATE_FORMAT;
}
